import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
import { Quote } from "lucide-react";
import { api } from "@/lib/api";
// Relative, not "@/": this module has no jest moduleNameMapper for "@/" (craco.config.js),
// and test suites that mount this component only shim the "@/" imports it already had —
// a real, un-mocked import must resolve natively, hence relative (cf. MinimalTemplate.jsx's
// identical note on its own "../lib/siteContent" import).
import { formatQuote } from "../../lib/testimonials";

// Anchor positions for popping cards (% relative to container, well distributed)
const POSITIONS = [
  { top: "4%", left: "2%", rotate: -3 },
  { top: "6%", right: "4%", rotate: 2.5 },
  { top: "32%", left: "0%", rotate: 1.5 },
  { top: "30%", right: "1%", rotate: -2 },
  { bottom: "4%", left: "4%", rotate: 2 },
  { bottom: "6%", right: "6%", rotate: -1.5 },
  { top: "2%", left: "38%", rotate: -1 },
  { bottom: "2%", left: "36%", rotate: 1.5 },
];

const COLORS = ["#F4EFE2"];

const PopCard = ({ item, position, color, onDone, index }) => {
  const { t } = useLanguage();
  const lifetime = 5500;
  useEffect(() => {
    const t = setTimeout(onDone, lifetime);
    return () => clearTimeout(t);
  }, [onDone]);

  const isAccent = color === "#E2A35B";
  const photo = item.photo_url || item.photo || item.image_url;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 18, rotate: position.rotate }}
      animate={{ opacity: 1, scale: 1, y: 0, rotate: position.rotate }}
      exit={{ opacity: 0, scale: 0.9, y: -10, transition: { duration: 0.5 } }}
      transition={{ type: "spring", stiffness: 110, damping: 16, mass: 0.6 }}
      style={{
        position: "absolute",
        top: position.top,
        bottom: position.bottom,
        left: position.left,
        right: position.right,
        background: color,
        maxWidth: 360,
      }}
      data-testid={`popup-testimonial-${index}`}
      className={`hidden md:block w-[300px] lg:w-[360px] rounded-3xl p-7 shadow-[0_24px_60px_-18px_rgba(0,0,0,0.45)] ${
        isAccent ? "text-eh-ink" : "text-eh-ink"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <Quote size={22} className={isAccent ? "text-eh-ink" : "text-eh-sage-deep"} />
        {photo && (
          <img
            src={photo}
            alt={item.name}
            className="w-10 h-10 rounded-full object-cover border border-eh-border-soft shadow-sm"
            data-testid={`popup-testimonial-photo-${index}`}
          />
        )}
      </div>
      <p className="mt-3 font-display italic text-lg md:text-xl leading-snug">
        {formatQuote(item.quote)}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="font-script text-2xl text-eh-ochre-deep">— {item.name}</div>
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`popup-review-link-${index}`}
            className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep hover:text-eh-ink underline underline-offset-4 decoration-eh-sage/60 hover:decoration-eh-ink transition"
            onClick={(e) => e.stopPropagation()}
          >
            {t.testimonials.readOnGoogle}
          </a>
        )}
      </div>
    </motion.div>
  );
};

const Testimonials = () => {
  const { t, lang } = useLanguage();
  const [remote, setRemote] = useState(null);
  // BUG-057 — THREE OUTCOMES, NOT TWO. `remote === null` used to mean both "this practice
  // has published none" and "the read failed", and both fell through to the bundled sample
  // copy below. So a 500, or the 403 a non-active practice answers every public read with,
  // put three invented quotes on the site of a practice that has twelve real ones. The
  // preview placeholders are deliberate for a practice that genuinely has none — they name
  // themselves samples — but substituting them for content we simply could not load is
  // putting words in a practitioner's clients' mouths.
  const [failed, setFailed] = useState(false);
  // Until the read settles the section renders NOTHING. Painting the samples first meant
  // every visitor saw "Sample testimonial" for the width of a round trip — including on a
  // practice with twelve real ones, whose quotes then appeared next to the invented ones,
  // because a card already spawned is not retired by a later `items` change.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/testimonials");
        // A non-array 200 is a real shape to survive, not paranoia: the production proxy
        // answers 200 + index.html for an unknown /api path, and reading that as "no
        // testimonials" is the same lie by a different route.
        if (!Array.isArray(data)) throw new Error("testimonials: unexpected payload shape");
        setFailed(false);
        if (data.length > 0) {
          setRemote(
            data.map((r) => ({
              name: r.name,
              quote: pickLocalized(r, "quote", lang),
              url: r.url || "",
              photo_url: r.photo_url || r.image_url || "",
            }))
          );
        }
      } catch (e) {
        setFailed(true);
        if (process.env.NODE_ENV !== "production") console.debug("[testimonials] remote load failed:", e?.message);
      } finally {
        setSettled(true);
      }
    })();
  }, [lang]);

  // On failure: NOTHING. A public visitor is not the person to report an infrastructure
  // fault to, and an absent section is honest where substituted content is not. Before the
  // read settles: also nothing, so the samples are never shown to someone who is about to
  // be shown the real ones.
  const items = useMemo(
    () => remote || (settled && !failed ? t.testimonials.items : []),
    [remote, settled, failed, t.testimonials.items],
  );
  const [cards, setCards] = useState([]);
  const idRef = useRef(0);

  const spawn = () => {
    setCards((prev) => {
      const usedPos = new Set(prev.map((c) => c.posIdx));
      const free = POSITIONS.map((_, i) => i).filter((i) => !usedPos.has(i));
      if (!free.length) return prev;
      const posIdx = free[Math.floor(Math.random() * free.length)];

      // avoid showing same testimonial as currently visible if possible
      const visibleItemIdx = new Set(prev.map((c) => c.itemIdx));
      const itemPool = items.map((_, i) => i).filter((i) => !visibleItemIdx.has(i));
      const itemIdx = (itemPool.length ? itemPool : items.map((_, i) => i))[
        Math.floor(Math.random() * (itemPool.length || items.length))
      ];
      const colorIdx = Math.floor(Math.random() * COLORS.length);
      idRef.current += 1;
      const newCard = {
        id: idRef.current,
        posIdx,
        itemIdx,
        position: POSITIONS[posIdx],
        item: items[itemIdx],
        color: COLORS[colorIdx],
      };
      const next = prev.length >= 3 ? [...prev.slice(1), newCard] : [...prev, newCard];
      return next;
    });
  };

  useEffect(() => {
    // CARDS ARE RETIRED WHEN THE SOURCE CHANGES. A card holds a COPY of its item, so one
    // spawned from a previous `items` outlives it — which is how a practice's real quotes
    // ended up on screen beside the sample ones, and how a failed read left samples
    // standing that the failure was supposed to remove.
    setCards([]);
    if (!items.length) return undefined;
    // Initial pop
    spawn();
    const t1 = setTimeout(spawn, 700);
    const t2 = setTimeout(spawn, 1400);
    const interval = setInterval(spawn, 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const remove = (id) => setCards((prev) => prev.filter((c) => c.id !== id));

  // BUG-227 — a failed load hides the whole section, not just its content. Everything
  // below (eyebrow, title, decorative hint, 620px popup zone) was rendering unconditionally,
  // so a failed read left the full-height band standing empty. `failed` is the one state
  // that must suppress the shell: `settled === false` (still loading) and `items.length === 0`
  // with `!failed` (genuinely no testimonials, BUG-057's preview case) both still render it.
  if (failed) return null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-36 bg-[#2C3531] text-eh-bg overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-eh-sage/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-eh-ochre/25 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="eyebrow" style={{ color: "#E2A35B" }}>
            {t.testimonials.eyebrow}
          </span>
          <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[1.05] whitespace-pre-line">
            {t.testimonials.title}
          </h2>
        </div>

        {/* Popup zone (desktop) */}
        <div className="relative mt-20 hidden md:block" style={{ height: "620px" }}>
          {/* Decorative center hint behind cards */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
            <div className="text-center">
              <div className="font-script text-6xl text-eh-ochre/50">avec gratitude</div>
            </div>
          </div>

          <div className="relative z-10 w-full h-full">
            <AnimatePresence>
              {cards.map((c, i) => (
                <PopCard
                  key={c.id}
                  item={c.item}
                  position={c.position}
                  color={c.color}
                  index={i}
                  onDone={() => remove(c.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile fallback — stacked */}
        <div className="md:hidden mt-12 space-y-5">
          {items.map((item, i) => {
            const photo = item.photo_url || item.photo || item.image_url;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-3xl p-7 text-eh-ink"
                style={{ background: COLORS[i % COLORS.length] }}
                data-testid={`mobile-testimonial-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Quote size={20} className="text-eh-sage-deep" />
                  {photo && (
                    <img
                      src={photo}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover border border-eh-border-soft shadow-sm"
                      data-testid={`mobile-testimonial-photo-${i}`}
                    />
                  )}
                </div>
                <p className="mt-3 font-display italic text-lg leading-snug">{formatQuote(item.quote)}</p>
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-script text-2xl text-eh-ochre-deep">— {item.name}</div>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`mobile-review-link-${i}`}
                      className="text-xs uppercase tracking-[0.18em] text-eh-sage-deep hover:text-eh-ink underline underline-offset-4"
                    >
                      {t.testimonials.readOnGoogle}
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
